import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Card, Form, Button, Spinner } from "react-bootstrap";
import {
    FaUser,
    FaEnvelope,
    FaLock,
    FaEye,
    FaEyeSlash,
    FaCheckCircle,
    FaExclamationTriangle,
    FaChevronDown,
} from "react-icons/fa";
import { authAPI } from "../services/Api";
import { useAuth } from "../context/AuthContext";
import logoImage from "../images/arogyamlogo.png";
import { motion } from "framer-motion";
import styles from "../css/AuthForms.module.css";

const Signup = () => {
    const navigate = useNavigate();
    useAuth();
    const [formData, setFormData] = useState({
        name: "",
        email: "",
        number: "",
        password: "",
        confirmPassword: "",
        referralCode: "",
    });
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState({});
    const [notification, setNotification] = useState(null);
    const [referralCodes, setReferralCodes] = useState([]);
    const [referralLoading, setReferralLoading] = useState(true);

    const showNotification = (message, type = "info") => {
        setNotification({ message, type });
        setTimeout(() => setNotification(null), 5000);
    };

    useEffect(() => {
        const loadReferralCodes = async () => {
            try {
                const codes = await authAPI.getReferralCodes();
                setReferralCodes(codes);
            } catch (error) {
                console.error("Referral codes load failed", error);
            } finally {
                setReferralLoading(false);
            }
        };

        loadReferralCodes();
    }, []);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
        if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
    };

    const emailRegex = /^[a-zA-Z0-9]+@gmail\.com$/;

    const validateForm = () => {
        const newErrors = {};
        if (!formData.name.trim()) {
            newErrors.name = "Name is required";
        } else if (formData.name.trim().length < 2 || formData.name.trim().length > 15) {
            newErrors.name = "Name must be between 2–15 characters";
        } else if (!/^[A-Za-z]+(?:\s{1,2}[A-Za-z]+)*$/.test(formData.name.trim())) {
            newErrors.name = "Name can only contain letters and max 2 spaces between words";
        }

        if (!formData.email.trim()) newErrors.email = "Email is required";
        else if (!emailRegex.test(formData.email.trim())) newErrors.email = "Only @gmail.com allowed";

        if (!formData.number.trim()) newErrors.number = "Phone number is required";
        else if (!/^[6-9]\d{9}$/.test(formData.number.trim()))
            newErrors.number = "Enter valid Indian mobile number";

        if (!formData.password) newErrors.password = "Password is required";
        else if (formData.password.length < 8)
            newErrors.password = "At least 8 characters required";
        else if (!/^[A-Z]/.test(formData.password))
            newErrors.password = "Password must start with capital letter";
        else if (!/[a-z]/.test(formData.password))
            newErrors.password = "Must include lowercase letter";
        else if (!/[A-Z]/.test(formData.password))
            newErrors.password = "Must include uppercase letter";
        else if (!/\d/.test(formData.password))
            newErrors.password = "Must include number";
        else if (!/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(formData.password))
            newErrors.password = "Must include special character";
        else if (!/^[A-Za-z\d!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]+$/.test(formData.password))
            newErrors.password = "Only English letters, numbers, and special characters allowed (no emoji)";


        if (!formData.confirmPassword) newErrors.confirmPassword = "Please confirm password";
        else if (formData.password !== formData.confirmPassword)
            newErrors.confirmPassword = "Passwords do not match";

        if (!formData.referralCode.trim()) newErrors.referralCode = "Referral code is required";
        else if (!/^[A-Za-z0-9_-]{4,20}$/.test(formData.referralCode.trim()))
            newErrors.referralCode = "Enter a valid referral code";

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;

        setLoading(true);
        try {
            const response = await authAPI.register({
                name: formData.name.trim(),
                email: formData.email.trim().toLowerCase(),
                number: formData.number.trim(),
                password: formData.password,
                referralCode: formData.referralCode.trim().toUpperCase(),
            });
            navigate("/login", {
                state: { message: "Account created! Please login.", email: response.email },
            });
        } catch (error) {
            showNotification(error.response?.data?.message || "Failed to create account", "error");
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            {notification && (
                <motion.div
                    className={`${styles.customNotification} ${notification.type === "success"
                        ? styles.customNotificationSuccess
                        : styles.customNotificationError
                        }`}
                    initial={{ opacity: 0, y: -30 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -30 }}
                    transition={{ duration: 0.3 }}
                >
                    <div>
                        {notification.type === "success" ? (
                            <FaCheckCircle className="me-2" />
                        ) : (
                            <FaExclamationTriangle className="me-2" />
                        )}
                        <span>{notification.message}</span>
                    </div>
                    <button className={styles.notificationClose} onClick={() => setNotification(null)}>
                        ×
                    </button>
                </motion.div>
            )}

            <div className={styles.mainContainer}>
                <motion.div
                    initial={{ opacity: 0, scale: 0.9, y: 30 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                >
                    <div className={styles.authShell}>
                        <div className={styles.heroPanel}>
                            <span className={styles.badge}>New on Arogyam?</span>
                            <h2 className={styles.heroTitle}>Create your account and shop essentials in a few taps.</h2>
                            
                            <div className={styles.heroHighlights}>
                                <span className={styles.highlightChip}>Quick signup</span>
                                <span className={styles.highlightChip}>Personalized orders</span>
                                <span className={styles.highlightChip}>Easy tracking</span>
                            </div>
                        </div>
                        <Card className={styles.card}>
                            <Card.Body className={styles.cardBody}>
                            <motion.div
                                className="text-center mb-4"
                                initial={{ opacity: 0, y: -20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.5, delay: 0.2 }}
                            >
                                <img src={logoImage} alt="Logo" className={styles.logoImage} />
                                <h2 className={styles.title}>Create Account</h2>
                                <p className={styles.subTitle}>Join our Arogyam Community</p>
                            </motion.div>

                            <Form onSubmit={handleSubmit}>
                                <Form.Group className="mb-3">
                                    <Form.Label className={styles.labelName}>
                                        <FaUser className="me-2" /> Full Name
                                    </Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="name"
                                        value={formData.name}
                                        onChange={handleChange}
                                        placeholder="Enter your full name"
                                        isInvalid={!!errors.name}
                                        className={styles.input}
                                    />
                                    <Form.Control.Feedback type="invalid">{errors.name}</Form.Control.Feedback>
                                </Form.Group>

                                <Form.Group className="mb-3">
                                    <Form.Label className={styles.labelName}>
                                        <FaEnvelope className="me-2" /> Email
                                    </Form.Label>
                                    <Form.Control
                                        type="email"
                                        name="email"
                                        value={formData.email}
                                        onChange={handleChange}
                                        placeholder="Enter your email"
                                        isInvalid={!!errors.email}
                                        className={styles.input}
                                    />
                                    <Form.Control.Feedback type="invalid">{errors.email}</Form.Control.Feedback>
                                </Form.Group>

                                <Form.Group className="mb-3">
                                    <Form.Label className={styles.labelName}>
                                        <FaUser className="me-2" /> Phone Number
                                    </Form.Label>
                                    <Form.Control
                                        type="tel"
                                        name="number"
                                        value={formData.number}
                                        onChange={handleChange}
                                        placeholder="Enter your phone number"
                                        isInvalid={!!errors.number}
                                        className={styles.input}
                                    />
                                    <Form.Control.Feedback type="invalid">{errors.number}</Form.Control.Feedback>
                                </Form.Group>

                                <Form.Group className="mb-3">
                                    <Form.Label className={styles.labelName}>
                                        <FaUser className="me-2" /> CHOOSE GROUP
                                    </Form.Label>
                                    {referralLoading ? (
                                        <div className="d-flex align-items-center gap-2">
                                            <Spinner animation="border" size="sm" />
                                            <span>Loading referral codes...</span>
                                        </div>
                                    ) : referralCodes.length > 0 ? (
                                        <>
                                            <div className={styles.selectWrapper}>
                                                <Form.Select
                                                    name="referralCode"
                                                    value={formData.referralCode}
                                                    onChange={handleChange}
                                                    isInvalid={!!errors.referralCode}
                                                    className={styles.input}
                                                >
                                                    <option value="">CHOOSE GROUP</option>
                                                    {referralCodes.map((ref) => (
                                                        <option key={ref.code} value={ref.code}>
                                                            {ref.code}
                                                        </option>
                                                    ))}
                                                </Form.Select>
                                                <FaChevronDown className={styles.selectIcon} />
                                            </div>
                                            <Form.Text className="text-muted">
                                                CHOOSE GROUP to get exclusive benefits and offers. If you don't have one, ask your group admin for the referral code.
                                            </Form.Text>
                                        </>
                                    ) : (
                                        <Form.Control
                                            type="text"
                                            name="referralCode"
                                            value={formData.referralCode}
                                            onChange={handleChange}
                                            placeholder="Enter group referral code"
                                            isInvalid={!!errors.referralCode}
                                            className={styles.input}
                                        />
                                    )}
                                    <Form.Control.Feedback type="invalid">{errors.referralCode}</Form.Control.Feedback>
                                </Form.Group>

                                <Form.Group className="mb-3">
                                    <Form.Label className={styles.labelName}>
                                        <FaLock className="me-2" /> Password
                                    </Form.Label>
                                    <div className={styles.passwordHint}>
                                        Password must start with a capital letter.
                                    </div>
                                    {errors.password && (
                                        <div className={styles.passwordError}>
                                            Please enter a strong password
                                        </div>
                                    )}
                                    <div className="position-relative">
                                        <Form.Control
                                            type={showPassword ? "text" : "password"}
                                            name="password"
                                            value={formData.password}
                                            onChange={handleChange}
                                            placeholder="Enter your password"
                                            isInvalid={!!errors.password}
                                            className={`${styles.input} pe-5`}
                                        />
                                        <button
                                            type="button"
                                            className={styles.eyeButton}
                                            onClick={() => setShowPassword(!showPassword)}
                                        >
                                            {showPassword ? <FaEyeSlash /> : <FaEye />}
                                        </button>
                                    </div>
                                </Form.Group>

                                <Form.Group className="mb-4">
                                    <Form.Label className={styles.labelName}>
                                        <FaLock className="me-2" /> Confirm Password
                                    </Form.Label>
                                    <div className="position-relative">
                                        <Form.Control
                                            type={showConfirmPassword ? "text" : "password"}
                                            name="confirmPassword"
                                            value={formData.confirmPassword}
                                            onChange={handleChange}
                                            placeholder="Confirm your password"
                                            isInvalid={!!errors.confirmPassword}
                                            className={`${styles.input} pe-5`}
                                        />
                                        <button
                                            type="button"
                                            className={styles.eyeButton}
                                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                        >
                                            {showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
                                        </button>
                                    </div>
                                    <Form.Control.Feedback type="invalid">
                                        {errors.confirmPassword}
                                    </Form.Control.Feedback>
                                </Form.Group>

                                <Button type="submit" variant="primary" size="lg" className={styles.submitBtn} disabled={loading}>
                                    {loading ? (
                                        <>
                                            <Spinner animation="border" size="sm" className="me-2" />
                                            Creating Account...
                                        </>
                                    ) : (
                                        "Create Account"
                                    )}
                                </Button>
                            </Form>

                            <hr className="my-4" />
                            <div className="text-center">
                                <p className={styles.p}>
                                    Already have an account?{" "}
                                    <Link to="/login" className={styles.loginLink}>
                                        Sign In
                                    </Link>
                                </p>
                            </div>
                            </Card.Body>
                        </Card>
                    </div>
                </motion.div>
            </div>
        </>
    );
};

export default Signup;
